/**
 * Proof Bank badge tokens — opaque hex, on the light theme, guarded by arithmetic.
 *
 * Same discipline as cadenceBadges.js and for the same reason: this app is a LIGHT theme
 * (hg-surface #FFFFFF, hg-bg #FAFAF7), and the badges this file replaces used dark-theme
 * Tailwind alpha utilities — `text-amber-300 bg-amber-400/10`, `text-blue-300`,
 * `text-red-300` — which composite to near-invisible. These badges carry the REASON a proof
 * cannot be used, so an illegible one puts the reader back to guessing.
 *
 * Every pairing is asserted >= 4.5:1 in cadenceBadges.contrast.test.js, which iterates this
 * file's exports too. Adding a blocker without a readable pairing breaks the build.
 */

// Blocker codes — must match proof_bank.BLOCKER_LABEL keys exactly. The test asserts the
// set is complete, so a new backend blocker with no front-end pairing fails rather than
// silently rendering as an unstyled fallback.
export const BLOCKERS = [
  'none',
  'withdrawn',
  'no_quote_text',
  'quote_too_short',
  'quote_but_no_comment_permission',
  'quote_but_no_naming_permission',
  'naming_declined',
  'granted_but_no_name_on_file',
];

// The neutral chips are gray-300, not gray-200. gray-200 (#E5E7EB) is legible enough for its
// text but only 1.13:1 against the withdrawn card's pink tint — a chip you can read floating
// on no visible chip. The contrast test caught that; the eye would not have.
//
// Three visual families, because the reader's next action differs:
//   green  — usable now, nothing to do
//   blue   — FIXABLE BY ASKING; this is the only family with an action attached
//   grey   — simply empty; no amount of asking creates a quote that was never written
//   red    — withdrawn; do not touch
export const BLOCKER_STYLE = {
  none: { fg: '#14532D', bg: '#86EFAC', family: 'ok' },
  quote_but_no_naming_permission: { fg: '#1E3A8A', bg: '#BFDBFE', family: 'askable' },
  quote_but_no_comment_permission: { fg: '#374151', bg: '#D1D5DB', family: 'empty' },
  no_quote_text: { fg: '#374151', bg: '#D1D5DB', family: 'empty' },
  quote_too_short: { fg: '#374151', bg: '#D1D5DB', family: 'empty' },
  naming_declined: { fg: '#4B5563', bg: '#D1D5DB', family: 'empty' },
  // Permission granted, no name on file. Fixable — but by finding the name, not by emailing
  // for consent — so it gets the askable family's blue without the askable action.
  granted_but_no_name_on_file: { fg: '#1E3A8A', bg: '#BFDBFE', family: 'fixable' },
  withdrawn: { fg: '#991B1B', bg: '#FECACA', family: 'withdrawn' },
};

// Attribution states — the three-way distinction the whole fix rests on. "Not asked" is
// styled as a NEUTRAL fact, deliberately not as a warning: nobody did anything wrong by not
// answering a question they were never shown, and colouring it like a problem would push
// someone toward treating it as a soft yes.
export const ATTRIBUTION_STYLE = {
  granted: { fg: '#14532D', bg: '#86EFAC', label: 'Name OK' },
  denied: { fg: '#4B5563', bg: '#D1D5DB', label: 'Name declined' },
  not_asked: { fg: '#374151', bg: '#D1D5DB', label: 'Name never asked' },
};

export const STATUS_STYLE = {
  available: { fg: '#14532D', bg: '#86EFAC', label: 'Available' },
  used: { fg: '#1E3A8A', bg: '#BFDBFE', label: 'Used' },
  withdrawn: { fg: '#991B1B', bg: '#FECACA', label: 'Withdrawn' },
};

// Quantitative material is usable NOW and needs no naming permission. It gets its own,
// quieter treatment so it reads as a separate kind of proof rather than a weaker testimonial.
export const QUANT_STYLE = { fg: '#1F3A5F', bg: '#DBEAFE' };

// Surface the badges sit on. Proof cards are plain hg-surface; a withdrawn card is tinted.
export const CARD_SURFACES = {
  default: '#FFFFFF',
  withdrawn: '#FEF2F2',
};

export const BADGE_FONT_PX = 11;

export function blockerStyle(code) {
  return BLOCKER_STYLE[code] || BLOCKER_STYLE.no_quote_text;
}

export function attributionStyle(state) {
  return ATTRIBUTION_STYLE[state] || ATTRIBUTION_STYLE.not_asked;
}

export function statusStyle(s) {
  return STATUS_STYLE[s] || STATUS_STYLE.available;
}
